import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { CampaignProspectType } from '../../api/campaigns';

interface CampaignTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (type: CampaignProspectType) => void;
}

const CampaignTypeModal: React.FC<CampaignTypeModalProps> = ({
  open,
  onClose,
  onSelect
}) => {
  const handleSelect = (type: CampaignProspectType) => {
    onSelect(type);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px'
        }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: '700',
          fontSize: '24px',
          padding: '24px 24px 16px 24px',
          color: (theme) => theme.customColors.themeNeutralPrimaryColor
        }}
      >
        Select Campaign Type
      </DialogTitle>
      <DialogContent sx={{ padding: '0 24px 24px 24px' }}>
        <Typography
          variant="body2"
          sx={{
            color: (theme) => theme.palette.text.secondary,
            marginBottom: '24px'
          }}
        >
          Choose a campaign type
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Accounts Card */}
          <Card
            sx={{
              flex: 1,
              border: '2px solid',
              borderColor: (theme) => theme.palette.divider,
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: (theme: Theme) =>
                  theme.customColors.spreadsheetAccountAccent,
                boxShadow: (theme: Theme) =>
                  `0 4px 12px ${theme.customColors.spreadsheetAccountAccent}40`,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardActionArea
              onClick={() => handleSelect(CampaignProspectType.ACCOUNT)}
            >
              <CardContent sx={{ padding: '24px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: (theme) =>
                        theme.customColors.themeTertiary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '4px'
                    }}
                  >
                    <BusinessIcon
                      sx={{
                        fontSize: '48px',
                        color: (theme) =>
                          theme.customColors.spreadsheetAccountAccent
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: '600',
                      color: (theme) =>
                        theme.customColors.themeNeutralPrimaryColor,
                      marginBottom: '4px'
                    }}
                  >
                    Accounts
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      lineHeight: 1.6
                    }}
                  >
                    Prospect by accounts through prioritizing companies, finding
                    net new accounts, tiering, and qualifying leads.
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>

          {/* Opportunities Card */}
          <Card
            sx={{
              flex: 1,
              border: '2px solid',
              borderColor: (theme) => theme.palette.divider,
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: (theme: Theme) =>
                  theme.customColors.spreadsheetOpportunityAccent,
                boxShadow: (theme: Theme) =>
                  `0 4px 12px ${theme.customColors.spreadsheetOpportunityAccent}40`,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardActionArea
              onClick={() => handleSelect(CampaignProspectType.OPPORTUNITY)}
            >
              <CardContent sx={{ padding: '24px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: (theme) =>
                        theme.customColors.themeTertiary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '4px'
                    }}
                  >
                    <AssignmentIcon
                      sx={{
                        fontSize: '48px',
                        color: (theme) =>
                          theme.customColors.spreadsheetOpportunityAccent
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: '600',
                      color: (theme) =>
                        theme.customColors.themeNeutralPrimaryColor,
                      marginBottom: '4px'
                    }}
                  >
                    Opportunities
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      lineHeight: 1.6
                    }}
                  >
                    Prospect by opportunities such as project links and RFPs.
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={onClose}
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            textTransform: 'none',
            fontWeight: '500'
          })}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignTypeModal;
